import React, { Component, Fragment, useContext } from 'react';
import {
  newUid,
  getSafe,
  deep_copy,
  last,
  orderWidgets,
  canAttemptCameraAccess,
  normalizeNbspHtml,
} from '@/util';
import { config } from '../../config';
import { withTranslation } from 'react-i18next';
import { CameraModal } from '@/components/CameraModal/CamereModal';
import { VideoCameraModal } from '@/components/CameraModal/VideoCameraModal';
import { DrawIoImportModal } from '@/components/DrawIoImportModal';
import { ContentLinkModal } from '@/components/ContentLinkModal';
import { AddImageToLibrary } from '@/components/AddImageToLibrary';
import { uploadFile, uploadImage } from '@/query/documents.js';
//import { connect } from 'react-redux';
import { axios } from '../../axios';
import i18n from 'i18next';
import { initFroala } from './froala';
import { decode, encode } from '@/base64';
//import { renderWidgets, renderWidget } from './widgets/renderWidget';
import {
  renderEditors,
  renderEditor,
  renderChatGpt,
  rerenderEditors,
} from './widgets/renderEditor';
import { renderWidgets } from './widgets/renderWidget';
import { uploadVideo } from '@/query/vimeo';
import { AuthContext } from '@/components/Contexts/AuthContext';
import update from 'immutability-helper';
import { LibraryModal } from '../LibraryModal/LibraryModal';
import { addCustomVideoLayer } from '@/components/Froala/insertVideoByUrl';
import { CourseLanguageContext } from '@/components/Contexts/CourseLanguageContext';
import {
  updateParagraphMenuActiveState,
  keepMoreOptionsMenuActiveInCodeView,
  updateColorToolbarButtons,
  updateColorToolbarButtonsDelayed,
  centerFroalaPopupUnderToolbar,
} from './utilFroala';
import { updateImagePopupDesign } from './toolbar/imageMenuHtml';
import { updateVideoPopupDesign } from './toolbar/videoMenuHtml';
import { updateLinkPopupDesign } from './toolbar/linkMenuHtml';
import { updateTablePopupDesign } from './toolbar/tableMenuHtml';

const MAX_SAVE_DELAY = 5000;

const VIMEO_IFRAME_ALLOW =
  'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share';

const VIDEO_ALIGNS = new Set(['left', 'center', 'right']);

function findElementInEditor(editor, uid) {
  const root = editor?.el;
  const elementId = String(uid || '');

  if (!root || !elementId) return null;
  if (root.id === elementId) return root;

  return root.querySelector(`#${CSS.escape(elementId)}`);
}

function mergeIframeAllow(existingAllow = '', extraAllow = VIMEO_IFRAME_ALLOW) {
  const parts = new Set();

  for (const chunk of `${existingAllow || ''};${extraAllow || ''}`.split(';')) {
    const value = chunk.trim();
    if (value) parts.add(value);
  }

  return Array.from(parts).join('; ');
}

function sizeValueToAttr(value) {
  const str = String(value || '').trim();
  if (!str) return '';

  const pxMatch = str.match(/^(\d+(?:\.\d+)?)px$/);
  if (pxMatch) return String(Math.round(Number(pxMatch[1])));

  const plainNumberMatch = str.match(/^(\d+(?:\.\d+)?)$/);
  if (plainNumberMatch) return String(Math.round(Number(plainNumberMatch[1])));

  const percentMatch = str.match(/^(\d+(?:\.\d+)?)%$/);
  if (percentMatch) return str;

  return '';
}

function getCurrentVideoAlign(span) {
  if (!span) return '';

  const styleAlign = String(span.style?.textAlign || '')
    .trim()
    .toLowerCase();

  if (VIDEO_ALIGNS.has(styleAlign)) return styleAlign;

  const attrAlign = String(span.getAttribute('align') || '')
    .trim()
    .toLowerCase();

  if (VIDEO_ALIGNS.has(attrAlign)) return attrAlign;

  const floatValue = String(span.style?.float || '')
    .trim()
    .toLowerCase();

  if (floatValue === 'left' || floatValue === 'right') {
    return floatValue;
  }

  return '';
}

function getPersistedVideoAlign(span) {
  if (!span) return '';

  const teoAlign = String(span.getAttribute('data-teo-video-align') || '')
    .trim()
    .toLowerCase();

  return VIDEO_ALIGNS.has(teoAlign) ? teoAlign : '';
}

function getVideoAlign(span) {
  if (!span) return 'center';

  // Prefer the current DOM style, because that reflects what the user just did.
  // The data attribute is only a fallback for older/sanitized embeds.
  return getCurrentVideoAlign(span) || getPersistedVideoAlign(span) || 'center';
}

function persistVideoAlign(span) {
  if (!span) return;

  const align = getVideoAlign(span);

  span.setAttribute('data-teo-video-align', align);
  span.style.textAlign = align;
}

function wrapRootLevelVideoInParagraph(span, root) {
  if (!span || !root || !span.parentNode) return;

  if (span.parentNode !== root) return;

  const p = document.createElement('p');
  root.insertBefore(p, span);
  p.appendChild(span);
}

function normalizeVimeoIframeSrc(src) {
  const value = String(src || '').trim();
  if (!value) return value;

  let url;

  try {
    url = new URL(value, window.location.href);
  } catch (_) {
    return value;
  }

  if (!url.hostname.endsWith('player.vimeo.com')) {
    return value;
  }

  // Make Vimeo's native controls explicitly available.
  url.searchParams.set('controls', '1');
  url.searchParams.set('fullscreen', '1');

  return url.toString();
}

function normalizeVideoSpan(span, root) {
  if (!span) return;

  const align = getVideoAlign(span);

  span.classList.add('fr-video', 'fr-deletable', 'fr-draggable');
  span.classList.remove('fr-active');

  span.setAttribute('contenteditable', 'false');
  span.setAttribute('draggable', 'true');
  span.setAttribute('data-teo-video-align', align);

  span.style.display = 'block';
  span.style.clear = 'both';
  span.style.textAlign = align;

  span.style.removeProperty('float');
  span.removeAttribute('align');

  if (!span.getAttribute('style')) {
    span.removeAttribute('style');
  }

  wrapRootLevelVideoInParagraph(span, root);
}

function normalizeVideoIframes(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  // First persist any existing deliberate alignments before touching iframes.
  root.querySelectorAll('span.fr-video').forEach((span) => {
    persistVideoAlign(span);
  });

  const iframes = root.querySelectorAll(
    [
      'span.fr-video iframe',
      'iframe[src*="player.vimeo.com"]',
      'iframe[id$="_placeholder"]',
    ].join(',')
  );

  iframes.forEach((iframe) => {
    const span = iframe.closest('span.fr-video');

    if (span) {
      normalizeVideoSpan(span, root);
    }

    const existingWidth = iframe.getAttribute('width');
    const existingHeight = iframe.getAttribute('height');

    const styleWidth = sizeValueToAttr(iframe.style?.width);
    const styleHeight = sizeValueToAttr(iframe.style?.height);

    const width = existingWidth || styleWidth || '640';
    const height = existingHeight || styleHeight || '360';

    const movedWidthFromStyle = !existingWidth && Boolean(styleWidth);
    const movedHeightFromStyle = !existingHeight && Boolean(styleHeight);

    const cleanIframe = iframe.cloneNode(false);

    cleanIframe.classList.add('fr-draggable');

    const normalizedSrc = normalizeVimeoIframeSrc(
      cleanIframe.getAttribute('src')
    );
    if (normalizedSrc) {
      cleanIframe.setAttribute('src', normalizedSrc);
    }

    cleanIframe.setAttribute('width', width);
    cleanIframe.setAttribute('height', height);

    cleanIframe.setAttribute(
      'allow',
      mergeIframeAllow(cleanIframe.getAttribute('allow'))
    );

    cleanIframe.setAttribute('allowfullscreen', '');
    cleanIframe.setAttribute('webkitallowfullscreen', '');
    cleanIframe.setAttribute('mozallowfullscreen', '');

    if (cleanIframe.style) {
      if (movedWidthFromStyle) cleanIframe.style.removeProperty('width');
      if (movedHeightFromStyle) cleanIframe.style.removeProperty('height');

      if (!cleanIframe.getAttribute('style')) {
        cleanIframe.removeAttribute('style');
      }
    }

    iframe.replaceWith(cleanIframe);
  });
}

function normalizeVideoHtml(html) {
  if (!html || typeof html !== 'string') return html;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;

  normalizeVideoIframes(wrapper);

  return wrapper.innerHTML;
}

const normalizeClipboardHtml = (clipboard_html) => {
  const temp = document.createElement('div');

  temp.innerHTML = clipboard_html;

  let root = temp;

  if (temp.children.length === 1 && temp.firstElementChild.tagName === 'DIV') {
    root = temp.firstElementChild;
  }

  const newHtml = [];

  [...root.childNodes].forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.replace(/\u00A0/g, ' ').trim();

      if (text) {
        newHtml.push(`<p>${text}</p>`);
      }
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      newHtml.push(node.outerHTML);
    }
  });

  const result = newHtml.join('').trim();

  if (!result) {
    return `<p contenteditable="true"></p>`;
  }

  return result;
};

const normalizeBrokenLists = (root) => {
  if (!root) return;

  root.querySelectorAll('ol, ul').forEach((list) => {
    const directLis = [...list.children].filter((el) => el.tagName === 'LI');

    directLis.forEach((li) => {
      const nestedLis = [...li.children].filter((el) => el.tagName === 'LI');

      [...nestedLis].reverse().forEach((badLi) => {
        list.insertBefore(badLi, li.nextSibling);
      });
    });
  });
};

//we het the latest state of all widgets in the copy pasted html
//and update localStorage.setItem('fr-copied-html', updated);
//in the paste function we get our content from the same localStorage
async function handleCopy(_this, editor, ev) {
  let clipboard_html = localStorage.getItem('fr-copied-html');
  if (clipboard_html) {
    try {
      const src = window.$.parseHTML(`<div>${clipboard_html}</div>`)[0];
      const placeholders = window.$.find('.widget-placeholder', src);
      let widgets = [];

      if (!placeholders) return true;
      for (const placeholder of placeholders) {
        let stateTag = placeholder.querySelectorAll(
          'div[data-state],span[data-state]'
        );
        let stateStr = stateTag[0].getAttribute('data-state');
        let state = JSON.parse(decode(stateStr));
        if (_this.state.editors[state.uid]) {
          let widget = _this.state.editors[state.uid];
          state = await widget.stateFn();
        }

        let newStateStr = encode(JSON.stringify(state));
        //update the DOM just in case you want to copy again

        // const node = document.querySelector(
        //   `#${CSS.escape(state.uid)} div[data-state],span[data-state]`
        // );

        if (!state?.uid) continue;
        const selector = `#${CSS.escape(
          state.uid
        )} div[data-state],span[data-state]`;
        const node = document.querySelector(selector);

        if (node) {
          node.setAttribute('data-state', newStateStr);
        }

        state.uid = newUid(20);
        placeholder.id = state.uid;
        placeholder.innerHTML = '';

        if (state.inline) {
          let child = document.createElement('span');
          child.setAttribute('data-open', 'SOW');
          0;
          child.setAttribute('data-state', newStateStr);
          child.innerHTML = '<span data-closed="EOW"></span>';
          placeholder.append(child);
        } else {
          let child = document.createElement('div');
          child.setAttribute('data-open', 'SOW');
          child.setAttribute('data-state', newStateStr);
          child.innerHTML = '<div data-closed="EOW"></div>';
          placeholder.append(child);
        }

        widgets.push({ placeholder, state });
      }
      localStorage.setItem('fr-copied-html', src.outerHTML);
    } catch (e) {
      console.error('pasting error', e);
    }
  }
}

async function sync() {
  return new Promise((resolve, reject) => {
    let promises = [];
    promises.push(
      new Promise((resolve) => {
        if (this.uploadPending === 0) {
          this.onUpdate();
          resolve();
        } else {
          let waitForUpload = setInterval(() => {
            if (this.uploadPending === 0) {
              this.onUpdate();
              resolve();
            }
          }, 200);
          setTimeout(() => {
            clearInterval(waitForUpload);
            console.error('Upload stil pending, giving up');
            window.$('.fr-uploading').remove();
            resolve();
          }, 20000);
        }
      })
    );

    for (let widget of Object.values(this.widgets)) {
      if (widget.refs[0].current && widget.refs[0].current.sync)
        promises.push(widget.refs[0].current.sync());
    }

    Promise.all(promises)
      .then(() => {
        let clone = window
          .$(this.myRef.current)
          .parent()
          .find('.fr-view')
          .clone();
        clone.find('.remove_me_gEE8QIPZ6LEay22SdSCU').remove();
        clone.find('div[data-open="SOW"]').empty();
        clone
          .find('div[data-open="SOW"]')
          .append('<div data-closed="EOW"></div>');
        normalizeVideoIframes(clone.get(0));
        let content = clone.html();
        if (this.state.editor) return resolve(content);
        return resolve();
      })
      .catch((e) => {
        return reject(e);
      });
  });
}

const quickInsertClick = () => {
  const quickInsert = document.getElementsByClassName('fr-quick-insert');
  const windowHeight = document.body.offsetHeight;
  if (quickInsert[0]) {
    const positionBtn = quickInsert[0].offsetTop;
    if (positionBtn + 700 > windowHeight) {
      quickInsert[0].classList.add('fr-quick-insert__bottom');
    } else {
      quickInsert[0].classList.remove('fr-quick-insert__bottom');
    }
  }
};

const blockFileDropInsideWidget = (e) => {
  const dt = e.dataTransfer;
  const hasFiles =
    dt &&
    ((dt.files && dt.files.length > 0) ||
      (dt.types && Array.from(dt.types).includes('Files')));

  let target = e.target;
  if (target?.nodeType === Node.TEXT_NODE) {
    target = target.parentElement;
  }

  const insideWidget =
    target instanceof Element && target.closest('.teo-block-drop');

  if (hasFiles && insideWidget) {
    e.preventDefault();
    e.stopPropagation();

    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }

    return false;
  }

  return true;
};

const restoreQuestionSelection = (editor) => {
  const savedRange = window.__QUESTION_SECTION_RANGE;

  if (!savedRange) return;

  try {
    const selection = window.getSelection();

    selection.removeAllRanges();
    selection.addRange(savedRange);
  } catch (e) {
    console.error(e);
  }
};

class FroalaTextarea extends Component {
  views = {};
  static contextType = AuthContext;

  constructor(props) {
    super(props);

    initFroala(); //start init asap cause componentDidMount will be waiting for it
    this.myRef = React.createRef();
    this.editorRef = React.createRef();
    this.canAccessCamera = props.reviewOnly
      ? Promise.resolve(false)
      : canAttemptCameraAccess();
    this.lessonId = props.lessonId;
    this.state = {
      editor: null,
      showWebcamPicture: null,
      showWebcamVideo: null,
      showDrawIoModal: null,
      showPictureLibrary: null,
      showImageLibraryForSelectedImage: false,
      editors: {},
      chatGpt: {},
      views: {},
      contentReady: false,
      uid: newUid(20),
    };

    this.unmount = {};
    this.widgets = {};
  }

  async getContent() {
    const root = document
      .getElementById(this.state.uid)
      ?.querySelector('.fr-view');

    //normalizeVideoIframes(root);

    const content = window
      .$(`#${CSS.escape(this.state.uid)}`)
      .find('.fr-view')
      .html();

    return content;
  }

  async componentWillUnmount() {
    /*
    if (this.props.onSave && !this.props.blockSaveOnUnmount)
      await this.props.onSave();
    //Whenever we enable this, we get issues, move with extreme caution
    //for (let node of Object.values(this.unmount)) {
    //  ReactDOM.unmountComponentAtNode(node);
    //}
    */
    // ---- ADD: cleanup listener ----
    try {
      if (this._onReplaceWidgetState) {
        window.removeEventListener(
          'froala:replace-widget-state',
          this._onReplaceWidgetState
        );
      }
      if (this.state.editor?.el && this._handleTextareaPaste) {
        this.state.editor.el.removeEventListener(
          'paste',
          this._handleTextareaPaste,
          true
        );
      }
      if (this._onUngroupWidget) {
        window.removeEventListener(
          'froala:ungroup-widget',
          this._onUngroupWidget
        );
      }
      if (this._onMoveWidgetToGroup) {
        window.removeEventListener(
          'froala:move-widget-to-group',
          this._onMoveWidgetToGroup
        );
      }

      if (this.state.editor?.el && this._blockFileDropInsideWidget) {
        this.state.editor.el.removeEventListener(
          'dragenter',
          this._blockFileDropInsideWidget,
          true
        );
        this.state.editor.el.removeEventListener(
          'dragover',
          this._blockFileDropInsideWidget,
          true
        );
        this.state.editor.el.removeEventListener(
          'drop',
          this._blockFileDropInsideWidget,
          true
        );
      }
      if (
        this.state.editor?.$box?.get?.(0) &&
        this._blockFileDropInsideWidget
      ) {
        const boxEl = this.state.editor.$box.get(0);
        boxEl.removeEventListener(
          'dragenter',
          this._blockFileDropInsideWidget,
          true
        );
        boxEl.removeEventListener(
          'dragover',
          this._blockFileDropInsideWidget,
          true
        );
        boxEl.removeEventListener(
          'drop',
          this._blockFileDropInsideWidget,
          true
        );
      }
    } catch (_) {
      //
    }
    // ---- END ADD ----
    if (this.state.editor) this.state.editor.destroy();
  }

  async componentDidMount() {
    const useTeoAi = this.context?.auth?.user?.useTeoAi;
    this.contentChanged = 0;
    this.props.setRef && this.props.setRef(this);
    await initFroala();

    let t = this.props.t;
    this.uploadPending = 0;

    let toolbarSticky = false;
    if (this.props.toolbarSticky) toolbarSticky = true;
    let toolbarButtons;
    let toolbarBtn;
    let fontSize = ['14', '18', '24'];

    switch (this.props.modus) {
      case 'text-only':
        toolbarBtn = {
          moreText: {
            buttons: [
              'bold',
              'italic',
              'underline',
              'strikeThrough',
              'subscript',
              'superscript',
              'textColor',
              'backgroundColor',
              'clearFormatting',
            ],
            buttonsVisible: 3,
          },
          moreParagraph: {
            buttons: ['formatOL', 'formatUL'],
            buttonsVisible: 2,
          },
          moreRich: {
            buttons: [
              'insertLink',
              'emoticons',
              'specialCharacters',
              'embedly',
              'insertHR',
              //'MathDropdown',
            ],
            buttonsVisible: 4,
          },
          moreMisc: {
            buttons: [
              'undo',
              'redo',
              'fullscreen',
              'selectAll',
              'html',
              'help',
            ], //'spellChecker',
            align: 'right',
            buttonsVisible: 3,
          },
        };
        toolbarButtons = toolbarBtn;
        break;
      case 'advanced':
        toolbarBtn = {
          moreText: {
            buttons: [
              'bold',
              'italic',
              'underline',
              'strikeThrough',
              'subscript',
              'superscript',
              'fontFamily',
              'fontSize',
              'textColor',
              'backgroundColor',
              'clearFormatting',
            ],
            buttonsVisible: 3,
          },
          moreParagraph: {
            buttons: [
              'alignLeft',
              'alignCenter',
              'formatOLSimple',
              'alignRight',
              'alignJustify',
              'formatOL',
              'formatUL',
              'paragraphFormat',
              'paragraphStyle',
              'lineHeight',
              'outdent',
              'indent',
              'quote',
            ],
            buttonsVisible: 3,
          },
          moreRich: {
            buttons: [
              'insertLink',
              'insertImage',
              'insertVideo',
              'insertTable',
              'emoticons',
              'specialCharacters',
              'embedly',
              'insertFile',
              'insertHR',
              'MathDropdown',
            ],
            buttonsVisible: 3,
          },
          moreMisc: {
            buttons: ['undo', 'redo', 'selectAll', 'html', 'help'], //'spellChecker',
            align: 'right',
            buttonsVisible: 2,
          },
        };
        toolbarButtons = toolbarBtn;
        break;
      case 'InvulVraag':
        toolbarBtn = {
          placeholder: {
            buttons: ['placeholder'],
            buttonsVisible: 1,
          },
          moreText: {
            buttons: [
              'bold',
              'italic',
              'underline',
              'strikeThrough',
              'subscript',
              'superscript',
              'fontFamily',
              'fontSize',
              'textColor',
              'backgroundColor',
              'clearFormatting',
            ],
            buttonsVisible: 3,
          },
          moreParagraph: {
            buttons: [
              'alignLeft',
              'alignCenter',
              'formatOLSimple',
              'alignRight',
              'alignJustify',
              'formatOL',
              'formatUL',
              'paragraphFormat',
              'paragraphStyle',
              'lineHeight',
              'outdent',
              'indent',
              'quote',
            ],
            buttonsVisible: 3,
          },
          moreRich: {
            buttons: [
              'insertLink',
              'insertImage',
              'insertVideo',
              'insertTable',
              'emoticons',
              'specialCharacters',
              'embedly',
              'insertFile',
              'insertHR',
              'MathDropdown',
            ],
            buttonsVisible: 3,
          },
          moreMisc: {
            buttons: ['undo', 'redo', 'selectAll', 'html', 'help'], //'spellChecker',
            align: 'right',
            buttonsVisible: 3,
          },
        };

        toolbarButtons = toolbarBtn;
        break;
      case 'lessonEdit':
        toolbarButtons = {
          moreTop: { buttons: ['undo', 'redo'] },
          moreSave: {
            buttons: ['save', 'fullscreen'],
          },
          moreQuiz: {
            buttons: [
              'InsertMenu',
              // 'MeerKeuze',
              // 'OpenVraag',
              // 'OrderVraag',
              // 'SortGroups',
              // 'ShowOnPicture',
              // 'Link',
              // 'MatrixVraag2',
              // 'NameImage2',
              // 'InvulVraag',
              // 'FileUpload',
              // 'KofferVraag2',
              // 'BoxError',
              //'KofferVraag'
            ],
            buttonsVisible: 100,
          },
          moreParagraphStyle: {
            buttons: ['ParagraphMenu'],
          },
          moreBodyText: {
            buttons: ['ParagraphFormatMenu'],
          },
          moreFormatText: {
            buttons: ['bold', 'italic', 'underline'],
          },
          moreStyleText: {
            buttons: ['textColor', 'backgroundColor'],
          },
          moreStrikeText: {
            buttons: ['ParagraphStriketMenu'],
          },
          moreText: {
            buttons: ['fontSize'],
          },
          moreAlignText: {
            buttons: ['ParagraphAlignMenu'],
          },
          moreListText: {
            buttons: ['ParagraphListMenu'],
          },

          moreMisc: {
            buttons: ['MoreOptionsMenu'],
            align: 'right',
            buttonsVisible: 1,
          },
        };

        fontSize = [
          '8',
          '9',
          '10',
          '11',
          '12',
          '14',
          '16',
          '18',
          '24',
          '30',
          '36',
          '48',
          '60',
          '72',
          '96',
        ];

        break;
      default:
        toolbarButtons = {
          moreParagraph: {
            buttons: [
              'bold',
              'italic',
              'underline',
              'fontSize',
              'color',
              'clearFormatting',
            ],
            buttonsVisible: 10,
          },
          moreRich: {
            buttons: [
              'insertLink',
              'insertImage',
              'insertVideo',
              'insertFile',
              'MathDropdown',
            ],
            buttonsVisible: 10,
          },
          moreListText: {
            buttons: ['ParagraphListMenu'],
          },
          moreTop: { buttons: ['undo', 'redo'] },
        };
        // toolbarButtons = [
        //   'bold',
        //   'italic',
        //   'underline',
        //   'fontSize',
        //   'color',
        //   // 'formatOL',
        //   // 'formatUL',
        //   'clearFormatting',
        //   'insertLink',
        //   'insertImage',
        //   'insertVideo',
        //   'insertFile',
        //   'MathDropdown',
        //   'ParagraphListMenu',
        //   'undo',
        //   'redo',
        // ]; //, 'getPDF'
        break;
    }

    if (this.props.reviewOnly) {
      toolbarButtons = {
        moreParagraphStyle: {
          buttons: ['ParagraphMenu'],
        },
        moreBodyText: {
          buttons: ['ParagraphFormatMenu'],
        },
        moreFormatText: {
          buttons: ['bold', 'italic', 'underline'],
        },
        moreStyleText: {
          buttons: ['textColor', 'backgroundColor'],
        },
        moreStrikeText: {
          buttons: ['ParagraphStriketMenu'],
        },
        moreText: {
          buttons: ['fontSize'],
        },
        moreAlignText: {
          buttons: ['ParagraphAlignMenu'],
        },
        moreListText: {
          buttons: ['ParagraphListMenu'],
        },
        moreMisc: {
          align: 'right',
          buttons: ['fullscreen'],
          buttonsVisible: 1,
        },
      };
    }

    //toolbarButtons['moreQuiz']['buttons'].splice(-1, 0, 'KofferVraag');
    //toolbarButtons['moreQuiz']['buttons'].splice(-1, 0, 'KofferVraag2');

    try {
      const user = this.context?.auth?.user;
      if (user && user.role >= 90 && toolbarButtons['moreQuiz']) {
        // toolbarButtons['moreQuiz']['buttons'].splice(-1, 0, 'KofferVraag');
      }
    } catch (e) {
      console.error(e);
    }

    let policy = this.props.reviewOnly ? null : await config.policy;

    if (!policy) {
      //this.props.dispatch(alertActions.error("S3_ERROR"));
    }

    let i = 0;

    // eslint-disable-next-line
    const _this = this;

    const canAccessCamera = await this.canAccessCamera;

    function generateSessionId() {
      let id = sessionStorage.getItem('froala_ai_thread_id');
      if (!id) {
        id = newUid(20);
        sessionStorage.setItem('froala_ai_thread_id', id);
      }
      return id;
    }
    if (!this.props.reviewOnly) {
      localStorage.setItem(
        'froala_ai_key',
        '<!--SET YOUR OPENAI KEY IN LOCALSTORAGE WITH THIS KEY-->'
      );
    }

    let editor = new window.FroalaEditor(this.myRef.current, {
      aiSupplementalTermsAccepted: true,

      aiAssistRequest: async function (data, signal) {
        const apiKey = localStorage.getItem('froala_ai_key');

        if (!apiKey) {
          throw new Error('Missing froala_ai_key in localStorage');
        }

        const prompt = [
          'Answer the question based on the context provided below. If the context is empty, answer the question based on your knowledge.',
          'Your response must be valid HTML only. Do not include markdown code fences or backticks.',
          'Preserve useful HTML formatting, tags, links, styles, and structure when relevant.',
          'If generating new content, use appropriate HTML tags.',
          'If the response includes code inside <pre> or <code>, ensure the outer <pre> tag has a data-code-snippet attribute with the language name and no class attribute.',
          'Do not add explanatory text outside the HTML response.',
          'Add additional whitespace after titles and paragraphs.',
          '',
          `Question: """${data.prompt || ''}"""`,
          '',
          'Answer:',
        ].join('\n');

        const response = await fetch('https://api.openai.com/v1/responses', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
          },
          signal,
          body: JSON.stringify({
            model: 'gpt-5.4-mini',
            input: prompt,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.error?.message || 'OpenAI request failed');
        }

        if (
          typeof result.output_text === 'string' &&
          result.output_text.trim()
        ) {
          return result.output_text;
        }

        const fallbackText =
          result?.output
            ?.flatMap((item) => item?.content || [])
            ?.filter((part) => part?.type === 'output_text')
            ?.map((part) => part?.text || '')
            ?.join('') || '';

        if (!fallbackText.trim()) {
          throw new Error('No text returned from OpenAI');
        }

        return fallbackText;
      },

      videoEditButtons: [
        ...(window.FroalaEditor?.DEFAULTS?.videoEditButtons || []),
        'generateSubtitles',
      ],
      courseLang: _this.props.courseLang || i18n.language || 'en',
      //theme: 'dark',
      quickInsertEnabled:
        !this.props.reviewOnly &&
        (!this.props.modus || this.props.modus === 'InvulVraag' ? false : true),
      quickInsertButtons: this.props.reviewOnly
        ? []
        : useTeoAi
        ? ['ChatGPT', 'imageLibraryHelp', 'image', 'videoLeft']
        : ['imageLibraryHelp', 'image', 'video'],
      quickInsertTags: [
        'p',
        'span',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'pre',
        'blockquote',
      ],
      zIndex: -1,
      attribution: false,
      indentWithTabs: true,
      tabSpaces: 4,
      imageUploadToS3: false,
      fileUploadToS3: this.props.reviewOnly ? null : policy,
      videoUploadToS3: this.props.reviewOnly ? null : policy,
      //enter: window.FroalaEditor.ENTER_BR,
      //iconsTemplate: 'font_awesome_5',
      // toolbarContainer: '#toolbarContainer',
      toolbarButtons: toolbarButtons,
      toolbarButtonsMD: toolbarButtons,
      imageInsertButtons: [
        'imageLibrary',
        'imageBack',
        '|',
        'imageUpload',
        'imageByURL',
        // 'imageManager',
        ...(canAccessCamera ? ['imageByCamera'] : []),
      ],
      videoInsertButtons: [
        'videoBack',
        '|',
        'videoByURL',
        'videoEmbed',
        'videoUpload',
        ...(canAccessCamera ? ['videoByCamera'] : []),
      ],
      key: 'qc1H2pI1B2C1E7C6F6C4A1AseueMRPYf1h1REb1BGQOQIc2CDBREJImA11C8D6F6B1G4E4F2F3A6==',
      //key: 'QFF4nB16A11A7A6D5C5E3fLUQZa1ASFe1EFRNc1He1BCCQDUHnD5D4B3J4C3A6D5C2C4F4==',
      //key: 'jUA1yD4A3B2A1G2B2F2qYFa1UQRFQIVc2MSMd1IWPNb1IFd1yD2I2D1B1C7E2A2C5D1C1==',
      //key: 'WE1B5dH4E3B2C9C10B5C5C-11VKOJ1FGULVKHXDXNDXc1d1Kg1SNdD5B4A4D3H3A2A2B7A4C3==',
      pluginsEnabled: Object.keys(window.FroalaEditor.PLUGINS).filter(
        (plugin) => plugin !== 'draggable'
      ),

      toolbarSticky: toolbarSticky,
      fileMaxSize: 1024 * 1024 * 50,
      importFromWordFileTypesAllowed: ['docx'],
      importFromWordMaxFileSize: 10 * 1024 * 1024,
      importFromWordEnableImportOnDrop: !this.props.reviewOnly,
      imageMaxSize: 1024 * 1024 * 50,
      toolbarStickyOffset: 0,
      htmlAllowedAttrs: ['.*'],
      htmlAllowedTags: ['.*'],
      htmlRemoveTags: ['script'],
      lineBreakerTags: [''],
      lineBreakerOffset: 0,
      linkAlwaysBlank: false,
      linkText: true,
      videoAllowedTypes: [
        'mp4',
        'webm',
        'ogg',
        // Lesson videos are transcoded by Vimeo; raw MOV files are not portable HTML video.
        ...(this.props.modus === 'lessonEdit' ? ['quicktime'] : []),
      ],
      imageAllowedTypes: [
        'jpeg',
        'jpg',
        'png',
        'gif',
        'webp',
        'avif',
        'apng',
        'heic',
        'heif',
        'heic-sequence',
        'heif-sequence',
        'svg',
        'svg+xml',
      ],
      htmlUntouched: true,
      videoDefaultAlign: 'center',
      imageDefaultAlign: 'center',
      pastePlain: false,
      //wordPasteModal: false,
      pasteAllowedStyleProps: [],
      pasteDeniedAttrs: ['style'],
      //pasteDeniedAttrs: [],
      tableStyles: {
        'fr-no-border': t('No borders'),
        'fr-dashed-borders': t('Dashed Borders'),
        'fr-alternate-rows': t('Alternate Rows'),
      },
      //placeholderText: t('Typ iets'),
      placeholderText: '',
      //height: getSafe(() => this.props.style.height, undefined),
      width: getSafe(() => this.props.style.width, undefined),
      heightMin: getSafe(() => this.props.style.minHeight, 120),
      widthMin: getSafe(() => this.props.style.minWidth, undefined),
      heightMax: getSafe(() => this.props.style.height, undefined),
      widthMax: getSafe(() => this.props.style.maxWidth, undefined),
      charCounterCount: false,
      //imageEditButtons: ['imageDisplay', 'imageAlign', 'imageInfo', 'imageRemove'],
      htmlAllowedEmptyTags: [
        'textarea',
        'a',
        'iframe',
        'object',
        'video',
        // 'style',
        'script',
        '.fa',
        '.fr-emoticon',
        '.fr-inner',
        'path',
        'line',
        'hr',
        'i',
        'div',
        'span',
        'input',
      ],
      //enter: window.FroalaEditor.ENTER_BR,
      fontSize: fontSize,
      language: i18n.language,
      events: {
        focus: function (e) {
          const selectionEl = this.selection?.element?.();

          const insideQuestionSection =
            selectionEl?.closest?.('.question_optional');

          // if focus is NOT inside widget section
          // clear saved nested selection
          if (!insideQuestionSection) {
            window.__QUESTION_SECTION_RANGE = null;
          }
        },
        initialized: function () {
          window.__ACTIVE_FROALA__ = this;

          this.opts.courseLang =
            _this.props.courseLang || i18n.language || 'en';
          _this.editorRef.current = this;

          setTimeout(() => {
            updateColorToolbarButtons(this);
          }, 0);
        },
        'image.beforeUpload': (images, existingImage) => {
          if (_this.props.reviewOnly) return false;

          const file = images?.[0];
          if (!file) return false;
          if (file.size > editor.opts.imageMaxSize) {
            editor.toast.show({ title: t('error.large_file'), type: 'error' });
            return false;
          }

          // TUI edits upload without passing Froala's replacement-image argument.
          const tuiEditorOpen = Boolean(
            editor.o_doc?.getElementById('tuieditor')
          );
          const selectedImage = tuiEditorOpen ? editor.image.get() : null;
          const replacementImage =
            (existingImage?.length && existingImage) ||
            (selectedImage?.length && selectedImage) ||
            null;

          _this.uploadPending += 1;
          editor.selection.save();
          const isSvg =
            file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
          const preserveAnimation = [
            'image/gif',
            'image/png',
            'image/apng',
            'image/webp',
            'image/avif',
          ].includes(file.type);
          const upload = isSvg
            ? uploadFile('uploaded', file)
            : uploadImage(file, 1920, 1080, 'webp', undefined, {
                preserveAnimation,
                withoutEnlargement: true,
              });

          upload
            .then((url) => {
              editor.events.focus();
              editor.selection.restore();
              editor.image.insert(url, true, null, replacementImage);
              editor.undo.saveStep();
              editor.events.trigger('contentChanged');
            })
            .catch((error) => {
              console.error('Image upload failed', error);
              editor.selection.restore();
              editor.toast.show({
                title: t('error.image_upload_failed'),
                type: 'error',
              });
            })
            .finally(() => {
              _this.uploadPending -= 1;
            });
          return false;
        },
        'file.beforeUpload': () => !_this.props.reviewOnly,
        'image.error': (error, response) => {
          console.error(error);
          _this.uploadPending -= 1;
          switch (error.code) {
            case 4:
              //this.props.dispatch(alertActions.fail('NETWORK_ERROR', 5000));
              window.$('.fr-uploading').remove();
              error.message = t('NETWORK_ERROR');
              break;
            case 5:
              var popup = window.$('.fr-error.fr-active .fr-message');
              popup[0].innerText = t('error.large_file');
              window.$('.fr-uploading').remove();
              break;
          }
        },
        'file.error': (error, response) => {
          _this.uploadPending -= 1;
          switch (error.code) {
            case 5:
              var popup = window.$('.fr-error.fr-active .fr-message');
              popup[0].innerText = t('error.large_file');
              window.$('.fr-uploading').remove();
              break;
          }
        },
        contentChanged: async function (ev) {
          _this.contentChanged = ++i;
          _this.onUpdate();
          _this.props.onModified && _this.props.onModified();
          _this.props.onChange &&
            _this.props.onChange({
              target: { value: await _this.getContent() },
            });
        },
        'html.set': function () {
          window.$('.remove_me_gEE8QIPZ6LEay22SdSCU').remove();
          normalizeVideoIframes(this.el);
          _this.findWidgets();
        },
        'commands.before': async function (cmd, _param1, _param2) {
          switch (cmd) {
            case 'redo':
            case 'undo':
              window.undoCanDo = this.undo.canDo();
              window.undoCanRedo = this.undo.canRedo();
              break;
            default:
              break;
          }
        },
        'window.copy': function (ev) {
          handleCopy(_this, this, ev);
        },
        'window.cut': function (ev) {
          handleCopy(_this, this, ev);
        },
        'popups.show.ParagraphFormatMenu': function () {
          setTimeout(() => {
            updateParagraphMenuActiveState(this);
          }, 0);
        },
        toolbarShow: function () {
          updateColorToolbarButtonsDelayed(this);
        },
        'commands.after': async function (cmd, _param1, _param2) {
          if (
            [
              'paragraphFormat',
              'paragraphStyle',
              'lineHeight',
              'indent',
              'outdent',
              'quote',
              'alignLeft',
              'alignCenter',
              'alignRight',
              'alignJustify',
              'strikeThrough',
              'superscript',
              'subscript',
              'clearFormatting',
              'ParagraphStriketMenu',
            ].includes(cmd)
          ) {
            setTimeout(() => {
              updateParagraphMenuActiveState(this);
            }, 0);
          }
          if (cmd === 'html') {
            setTimeout(() => {
              keepMoreOptionsMenuActiveInCodeView(this);
              updateParagraphMenuActiveState(this);
            }, 0);
          }
          if (cmd === 'applytextColor') {
            updateColorToolbarButtonsDelayed(this, {
              textColor: _param1,
            });
          }

          if (cmd === 'applybackgroundColor') {
            updateColorToolbarButtonsDelayed(this, {
              backgroundColor: _param1,
            });
          }

          if (
            [
              'bold',
              'italic',
              'underline',
              'strikeThrough',
              'superscript',
              'subscript',
              'clearFormatting',
            ].includes(cmd)
          ) {
            updateColorToolbarButtonsDelayed(this);
          }
          switch (cmd) {
            case 'fullscreen':
              try {
                window.dispatchEvent(new Event('resize'));
              } catch (e) {
                console.error(e);
              } //some browsers don't support this
              break;
            case 'redo':
            case 'undo':
              if (window.undoCanDo) {
                const widgets = [];
                const chats = [];
                for (const widget of Object.values(_this.state.editors)) {
                  widgets.push(await widget.stateFn());
                }
                for (const chat of Object.values(_this.state.chatGpt)) {
                  chats.push(chat.state);
                }

                const newWidgets = Object.values(
                  rerenderEditors.call(
                    null,
                    this.el,
                    this.undo,
                    this.lessonId,
                    widgets,
                    chats
                  )
                ).filter((x) => x);

                if (widgets.length > 0) {
                  _this.setState({
                    contentReady: true,
                    editors: newWidgets[0].reduce((acc, el) => {
                      acc[el.state.uid] = el;
                      return acc;
                    }, {}),
                  });
                }
                setTimeout(() => {
                  _this.setState({
                    contentReady: true,
                    chatGpt: newWidgets[1].reduce((acc, el) => {
                      acc[el.state.uid] = el;
                      return acc;
                    }, {}),
                  });
                }, 10);
              }
              break;
            default:
              break;
          }
        },
        'paste.after': function () {
          setTimeout(() => {
            normalizeVideoIframes(this.el);

            const questionOptionals =
              document.querySelectorAll('.question_optional');

            const isEmptyContent = (element) => {
              if (!element) return true;

              const clone = element.cloneNode(true);

              const text = clone.textContent.replace(/\u200B/g, '').trim();

              const hasTags = clone.children.length > 0;

              return !text && !hasTags;
            };

            this.el
              .querySelectorAll('.question_optional .question_optional')
              .forEach((nested) => {
                const outer = nested.parentElement?.closest(
                  '.question_optional, .widget-placeholder'
                );
                if (outer?.classList.contains('question_optional')) {
                  nested.replaceWith(...nested.childNodes);
                }
              });

            questionOptionals.forEach((item) => {
              if (isEmptyContent(item)) {
                item.innerHTML = `<p contenteditable="true"></p>`;
              }
            });
          }, 0);
        },
        'paste.before': function (ev) {
          if (document.activeElement instanceof HTMLTextAreaElement) {
            return false;
          }
          this.undo.saveStep();
          let clipboard_text = ev.clipboardData.getData('Text');
          if (clipboard_text) {
            if (clipboard_text.startsWith('widget:')) {
              const stateStr = clipboard_text.split(':')[1];
              const state = JSON.parse(decode(stateStr));
              state.uid = newUid(20);
              editor.insertWidget(null, state);
              _this.props.onModified();
              setTimeout(() => {
                orderWidgets();
              });
              return false;
            }
          }

          let clipboard_html = ev.clipboardData.getData('text/html');

          if (clipboard_html) {
            try {
              let src = window.$.parseHTML(`<div>${clipboard_html}</div>`)[0];
              let placeholders = window.$.find('.widget-placeholder', src);
              if (!placeholders || placeholders.length === 0) return true;

              //use the updated version that we put on the localStorage in handleCopy
              clipboard_html = localStorage.getItem('fr-copied-html');
              src = window.$.parseHTML(`<div>${clipboard_html}</div>`)[0];
              placeholders = window.$.find('.widget-placeholder', src);
              let widgets = [];

              for (const placeholder of placeholders) {
                let stateTag = placeholder.querySelectorAll(
                  'div[data-state],span[data-state]'
                );
                let stateStr = stateTag[0].getAttribute('data-state');
                let state = JSON.parse(decode(stateStr));
                state.uid = newUid(20);
                widgets.push();
                placeholder.id = state.uid;
                placeholder.innerHTML = '';
                if (state.inline) {
                  let child = document.createElement('span');
                  child.setAttribute('data-open', 'SOW');
                  child.setAttribute(
                    'data-state',
                    encode(JSON.stringify(stateStr))
                  );
                  child.innerHTML = '<span data-closed="EOW"></span>';
                  placeholder.append(child);
                } else {
                  let child = document.createElement('div');
                  child.setAttribute('data-open', 'SOW');
                  child.setAttribute(
                    'data-state',
                    encode(JSON.stringify(stateStr))
                  );
                  child.innerHTML = '<div data-closed="EOW"></div>';
                  placeholder.append(child);
                }
                widgets.push({ placeholder, state });
              }
              this.html.insert(src.outerHTML);
              setTimeout(() => {
                let editors = _this.state.editors;
                for (let widget of widgets) {
                  _this.setState({ editors }, () => {
                    _this.props.onModified();
                  });
                  editors = update(editors, {
                    [widget.state.uid]: {
                      $set: renderEditor(
                        widget.state,
                        findElementInEditor(
                          _this.state.editor,
                          widget.state.uid
                        ),
                        _this.state.editor.undo,
                        _this.lessonId
                      ),
                    },
                  });
                }
                _this.setState({ editors }, () => {
                  _this.props.onModified();
                });
              }, 0);
              return false;
            } catch (e) {
              console.error('pasting error', e);
            }
          }
          return true;
        },
        'popups.show.image.edit': async function () {
          const orgID = _this.props.authUser.activeOrganisationId;
          const $img = editor.image?.get?.();
          const img = $img?.length ? $img.get(0) : null;
          const imageUrl = img.getAttribute('src') || '';
          const isAmazonAws = imageUrl?.includes('amazonaws.com');
          const isCloudFront = imageUrl?.includes('cloudfront.net');

          let uid;
          let imgName;
          let imgPrev;

          if (isAmazonAws) {
            const fileName = imageUrl.split('/').pop();
            imgPrev = fileName.substring(fileName.indexOf('-') + 1);
            imgName = imgPrev.substring(0, imgPrev.lastIndexOf('.'));
            const decodedURL = decodeURIComponent(imageUrl);
            const fileTitle = decodedURL.split('/').pop();
            uid = fileTitle.split('-')[0];
          }

          if (isCloudFront) {
            const fileName = imageUrl.split('/').pop();
            imgPrev = fileName.substring(fileName.indexOf('-') + 1);
            imgName = imgPrev.substring(0, imgPrev.lastIndexOf('.'));
          }
          const imageUid = isAmazonAws ? uid : isCloudFront ? imgName : uid;

          let isEdit = false;

          await axios
            .get(
              config.backend +
                '/imagelibraries?organisationId=' +
                orgID +
                '&imageUid=' +
                imageUid
            )
            .then((response) => {
              if (response?.data?.length > 0) {
                isEdit = true;
              }
            });

          updateImagePopupDesign(this, isEdit);
        },
        'popups.show.video.edit': function () {
          updateVideoPopupDesign(this);
        },
        'popups.show.link.edit': function () {
          updateLinkPopupDesign(this);
        },
        'popups.show.table.edit': function () {
          updateTablePopupDesign(this);
        },
        'popups.show.forms.edit': function () {
          this.popups.hideAll();
          return false;
        },
        'popups.show.link.insert': function () {
          restoreQuestionSelection(this);
          centerFroalaPopupUnderToolbar(this, 'link.insert');
        },

        'popups.show.image.insert': function () {
          restoreQuestionSelection(this);
          centerFroalaPopupUnderToolbar(this, 'image.insert');
        },

        'popups.show.video.insert': function () {
          restoreQuestionSelection(this);
          const editor = this;

          setTimeout(() => {
            const popup = editor.popups.get('video.insert')?.get?.(0);

            if (popup && !popup.querySelector('.fr-custom-layer')) {
              addCustomVideoLayer(editor);
            }

            centerFroalaPopupUnderToolbar(editor, 'video.insert');
          }, 0);
        },

        'popups.show.table.insert': function () {
          setTimeout(() => {
            restoreQuestionSelection(this);
          }, 0);
          centerFroalaPopupUnderToolbar(this, 'table.insert');
        },

        'popups.show.emoticons': function () {
          setTimeout(() => {
            restoreQuestionSelection(this);
          }, 0);
          centerFroalaPopupUnderToolbar(this, 'emoticons');
        },

        'popups.show.specialCharacters': function () {
          setTimeout(() => {
            restoreQuestionSelection(this);
          }, 0);
          centerFroalaPopupUnderToolbar(this, 'specialCharacters');
        },

        'popups.show.file.insert': function () {
          centerFroalaPopupUnderToolbar(this, 'file.insert');
        },

        'popups.show.iframelyPlugin.popup': function () {
          centerFroalaPopupUnderToolbar(this, 'iframelyPlugin.popup');
        },

        'popups.show.d3Plugin.popup': function () {
          centerFroalaPopupUnderToolbar(this, 'd3Plugin.popup');
        },

        'popups.show.image.size': function () {
          centerFroalaPopupUnderToolbar(this, 'image.size');
        },

        'popups.show.video.size': function () {
          centerFroalaPopupUnderToolbar(this, 'video.size');
        },

        'popups.show.image.alt': function () {
          centerFroalaPopupUnderToolbar(this, 'image.alt');
        },

        'paste.beforeCleanup': function (clipboard_html) {
          if (!clipboard_html) return clipboard_html;
          clipboard_html = clipboard_html.replace(/<meta[^>]*>/gi, '');
          const parser = new DOMParser();
          const doc = parser.parseFromString(clipboard_html, 'text/html');
          const firstChild = doc.body.firstElementChild;
          const hasPTags = doc.querySelectorAll('p').length > 0;
          const spansTags = doc.querySelectorAll('span');
          if (
            firstChild &&
            firstChild.tagName.toLowerCase() === 'span' &&
            !hasPTags
          ) {
            let newValue;
            spansTags.forEach((child) => {
              const newSpan = newValue;
              const sourceStyles = child.getAttribute('style');
              const spanElement = document.createElement('span');
              spanElement.setAttribute('style', sourceStyles);
              spanElement.innerHTML = child.innerHTML;
              newValue = newSpan;
            });
            const htmlString = newValue?.outerHTML;
            return htmlString;
          }
          this._lastPastedListTag = null;

          this._lastPastedListStyleType = null;

          if (
            firstChild &&
            ['ol', 'ul'].includes(firstChild.tagName.toLowerCase())
          ) {
            this._lastPastedListTag = firstChild.tagName.toLowerCase();

            const style =
              firstChild.style.listStyleType ||
              window.getComputedStyle(firstChild).listStyleType;
            this._lastPastedListStyleType = style;
            const cleanList = document.createElement(this._lastPastedListTag);
            if (style) {
              cleanList.style.listStyleType = style;
            }
            firstChild.querySelectorAll('li').forEach((li) => {
              const newLi = document.createElement('li');
              newLi.innerHTML = li.innerHTML;
              cleanList.appendChild(newLi);
            });
            return cleanList.outerHTML;
          }

          return clipboard_html;
        },
        'paste.afterCleanup': function (clipboard_html) {
          if (clipboard_html) {
            clipboard_html = normalizeNbspHtml(clipboard_html);

            const selectionEl = this.selection?.element?.();
            const insideWidgetPlaceholder = selectionEl?.closest?.(
              '.widget-placeholder'
            );

            const insideInstructionStep =
              selectionEl?.closest?.('.instruction_step');

            const questionField = selectionEl?.classList?.contains(
              'question_optional'
            )
              ? selectionEl
              : (() => {
                  const field = selectionEl?.closest?.('.question_optional');
                  const isEmpty =
                    field &&
                    !field.textContent.replace(/\u200B/g, '').trim() &&
                    !field.querySelector('img, video, iframe, ._katex');
                  return isEmpty ? field : null;
                })();

            if (questionField && insideWidgetPlaceholder) {
              const isTextItemMc =
                questionField?.classList?.contains('text-item-mc');
              if (isTextItemMc) {
                const id = questionField?.id || '';

                const cleanedHtml = normalizeClipboardHtml(clipboard_html);

                return `
          <div 
            id="${id}" 
            class="question_optional fr-view min-h-7 cursor-text text-item-mc" 
            contenteditable="true"
          >
            ${cleanedHtml}
          </div>
        `;
              }

              return `
        <div class="question_optional fr-view min-h-12 cursor-text" contenteditable="true">
          <p>${clipboard_html}</p>
        </div>
      `;
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(clipboard_html, 'text/html');

            const liTags = doc.querySelectorAll('li');

            const hasBrokenList = liTags.length && !doc.querySelector('ol, ul');

            if (hasBrokenList) {
              const listTag = this._lastPastedListTag || 'ol';

              const list = document.createElement(listTag);

              if (this._lastPastedListStyleType) {
                list.style.listStyleType = this._lastPastedListStyleType;
              }

              liTags.forEach((li) => {
                const newLi = document.createElement('li');

                newLi.innerHTML = li.innerHTML;

                list.appendChild(newLi);
              });
              return list.outerHTML;
            }

            if (insideInstructionStep) {
              const parser = new DOMParser();

              const doc = parser.parseFromString(clipboard_html, 'text/html');

              const onlyDivs =
                doc.body.children.length === 1 &&
                doc.body.firstElementChild?.tagName === 'DIV';

              if (onlyDivs) {
                let current = doc.body.firstElementChild;

                while (
                  current &&
                  current.children.length === 1 &&
                  current.firstElementChild?.tagName === 'DIV'
                ) {
                  current = current.firstElementChild;
                }

                const structured = current.querySelector('p, ol, ul, table');

                if (structured) {
                  return current.innerHTML;
                }

                const text = current?.textContent?.trim();

                if (text) {
                  return `<p>${text}</p>`;
                }
              }
            }

            const tables = doc.querySelectorAll('table');
            if (tables[0]?.querySelectorAll('td, th')?.length === 1) {
              return tables[0]?.querySelectorAll('td, th')[0].innerHTML;
            }
            return clipboard_html;
          }
        },

        /*
        'paste.afterCleanup': function (clipboard_html) {
          if (clipboard_html.search('widget-placeholder')) {
            setTimeout(() => this.findWidgets(), 0);
            return `<p><br></p>${clipboard_html}<p><br></p>`;
          }
        },
        */
        keyup: async function (ev) {
          if (ev.key === 'Delete') {
            requestAnimationFrame(() => {
              normalizeBrokenLists(this.el);
            });
          }
          setTimeout(() => {
            updateColorToolbarButtons(this);
          }, 0);
          _this.contentChanged = ++i;
          var ctrlKey =
            navigator.userAgent.indexOf('Mac OS X') !== -1
              ? ev.metaKey
                ? ev.metaKey
                : ev.ctrlKey
              : ev.ctrlKey;
          if (ctrlKey) {
            switch (ev.key) {
              case 's':
                if (_this.props.onSave) await _this.props.onSave();
                break;
              case 'a':
                ev.preventDefault();
                var currentElement = editor.selection.element();
                var frElementParent = currentElement.closest('.fr-element');
                var selection = window.getSelection();
                var range = document.createRange();
                range.selectNodeContents(frElementParent);
                selection.removeAllRanges();
                selection.addRange(range);
                break;
            }
          }
          if (ev.key == 'Enter' || ev.key == 'ArrowDown') {
            quickInsertClick();
          }
          return true;
        },
        click: () => {
          quickInsertClick();
          updateColorToolbarButtonsDelayed(this);
        },
        mouseup: function () {
          updateColorToolbarButtonsDelayed(this);
        },
        'video.error': function (error, response) {
          console.error('video error', error, response);
        },
        'video.inserted': function () {
          normalizeVideoIframes(this.el);
        },
        'video.uploadedToS3': function (url) {
          this.html.insert(
            `<p><br></p><p><span contenteditable="false" draggable="true" class="fr-video fr-dvb fr-draggable fr-active"><video src="${url}" style="width: 600px;" controls="" class="fr-draggable">Je browser ondersteunt geen html5-video.</video></span></p><p><br></p>`,
            true
          );
          return false;
        },
        'video.beforeUpload': function (videos) {
          if (_this.props.reviewOnly) return false;
          if (_this.props.modus !== 'lessonEdit') return true;
          setTimeout(
            ((videos) => {
              const popup = editor.popups.get('video.insert');
              let autoPlay = false;
              let autoLoop = false;
              if (popup && popup[0]) {
                const videoUploadLayer = popup[0].querySelector(
                  '.fr-video-upload-layer'
                );
                if (videoUploadLayer) {
                  autoPlay = videoUploadLayer.querySelector(
                    '#fr-videoUploadAutoplay'
                  ).checked;
                  videoUploadLayer.querySelector(
                    '#fr-videoUploadAutoplay'
                  ).checked = false;

                  autoLoop = videoUploadLayer.querySelector(
                    '#fr-videoUploadAutoLoop'
                  ).checked;
                  videoUploadLayer.querySelector(
                    '#fr-videoUploadAutoLoop'
                  ).checked = false;
                }
              }

              let uid = newUid(20);
              // let uri = "";
              let onProgress = (_progress, percentage, status) => {
                window.$('#' + CSS.escape(uid)).width(percentage + '%');
                if (status) {
                  window.$('#' + CSS.escape(uid)).text(status);
                } else if (percentage < 100) {
                  window
                    .$('#' + CSS.escape(uid))
                    .text(
                      percentage +
                        t(
                          "% (you can continue to edit while uploading, but don't leave the lesson"
                        )
                    );
                }
              };
              let onDone = () => {
                _this.uploadPending -= 1;
                window
                  .$('#' + CSS.escape(uid))
                  .text(
                    t(
                      'Upload complete, encoding. Video will be available in 2-60 min, but you can leave the lesson'
                    )
                  );
                function checkIfVideoExists(url, callback) {
                  let id = last(url.split('/'));
                  axios
                    .get(config.backend + '/vimeo/status/' + id)
                    .then((response) => {
                      if (
                        response.data.upload.status === 'complete' &&
                        response.data.transcode.status === 'complete'
                      ) {
                        window.$('.remove_me_gEE8QIPZ6LEay22SdSCU').remove();
                        callback(response.data);
                      }
                    });
                }
                let tryLoad = () => {
                  let iframe = document.getElementById(uid + '_placeholder');
                  try {
                    checkIfVideoExists(iframe.src, function () {
                      let iframe = document.getElementById(
                        uid + '_placeholder'
                      );
                      clearInterval(tryLoadInterval);
                      setTimeout(() => {
                        window
                          .$('#' + CSS.escape(uid))
                          .parent()
                          .remove();
                        iframe.src = iframe.src; // eslint-disable-line
                      }, 30000);
                    });
                  } catch (e) {
                    console.error(e);
                  }
                };
                let tryLoadInterval = setInterval(() => {
                  tryLoad();
                }, 10000);
                window.$('#' + CSS.escape(uid)).click(() => {
                  tryLoad();
                });
              };
              _this.uploadPending += 1;
              this.html.insert(
                `<p><br /></p><p><div contenteditable="false" class="fr-deletable remove_me_gEE8QIPZ6LEay22SdSCU mx-auto" style="cursor: pointer; text-align: center; font-size: 12px; height: 20px; width: 640px; background-color: #BBBBBB";><div style="height:20px; background-color: #fccc4a; white-space: nowrap; overflow: visible;" id="${uid}"></div></div>
              <span id="${uid}_embed" class="fr-video fr-deletable fr-draggable" contenteditable="false" draggable="true" style="display: block; clear: both; text-align: center;"><iframe id="${uid}_placeholder" width="640" height="360" frameborder="0" allow="${VIMEO_IFRAME_ALLOW}" allowfullscreen="" webkitallowfullscreen="" mozallowfullscreen="" class="fr-draggable"></iframe></span></p><p><br /></p>`,
                true
              );
              this.undo.saveStep();

              onProgress(1, 100, t('Preparing'));

              for (let file of videos) {
                let title;
                try {
                  title = file.name.split('.').slice(0, -1).join('.');
                } catch (e) {
                  title = document.title;
                }
                uploadVideo(file, onProgress, onDone, title)
                  .then((uri) => {
                    if (autoLoop) {
                      uri += '?&loop=1';
                      if (autoPlay) {
                        uri += '&autoplay=1&muted=1&#t=0s';
                      }
                    } else if (autoPlay) {
                      uri += '?&autoplay=1&muted=1&#t=0s';
                    }
                    document.getElementById(uid + '_placeholder').src = uri;
                  })
                  .catch((e) => console.error(e));
              }
            }).bind(this, [...videos]),
            0
          );

          this.popups.hideAll();
          return false;
        },
        blur: function () {
          _this.props.onBlur && _this.props.onBlur();
        },
      },
    });

    this._handleTextareaPaste = (ev) => {
      if (!(ev.target instanceof HTMLTextAreaElement)) return;

      ev.stopPropagation();
      ev.stopImmediatePropagation();
    };

    editor.el.addEventListener('paste', this._handleTextareaPaste, true);

    editor.el.addEventListener('dragenter', blockFileDropInsideWidget, true);
    editor.el.addEventListener('dragover', blockFileDropInsideWidget, true);
    editor.el.addEventListener('drop', blockFileDropInsideWidget, true);
    editor.save_cb = this.props.reviewOnly ? undefined : this.props.onSave;
    editor.placeholder_cb = this.props.onPlaceholder;
    editor.insertWidget = this.props.reviewOnly ? undefined : this.insertWidget;
    editor.insertChatGpt = this.props.reviewOnly
      ? undefined
      : this.insertChatGpt;
    editor.insertPhet = this.props.reviewOnly ? undefined : this.insertPhet;
    editor.insertInlineWidget = this.props.reviewOnly
      ? undefined
      : this.insertInlineWidget;
    editor.insertEmbedWidget = this.props.reviewOnly
      ? undefined
      : this.insertEmbedWidget;
    editor.moreExec = this.moreExec;
    editor.toggleMoreButton = this.toggleMoreButton;
    editor.insertCamera = this.props.reviewOnly
      ? undefined
      : () => this.setState({ showWebcamPicture: true });
    editor.insertLibrary = this.props.reviewOnly
      ? undefined
      : () => this.setState({ showPictureLibrary: true });
    editor.insertVideoCamera = this.props.reviewOnly
      ? undefined
      : () => this.setState({ showWebcamVideo: true });
    editor.insertDrawIoLink = this.props.reviewOnly
      ? undefined
      : () => this.setState({ showDrawIoModal: true });
    editor.insertContentLink = this.props.reviewOnly
      ? undefined
      : () => {
          editor.selection.save();
          this.setState({
            showContentLinkModal: {
              text: editor.selection.text?.() || '',
            },
          });
        };
    editor.openImageLibraryForSelectedImage = this.props.reviewOnly
      ? undefined
      : () => {
          const $img = editor.image?.get?.();
          const img = $img?.length ? $img.get(0) : null;

          if (!img) return;

          editor.selection?.save?.();
          editor.popups?.hideAll?.();

          this.setState({
            showImageLibraryForSelectedImage: true,
            selectedImageUrl: img.getAttribute('src') || '',
          });
        };

    this.started = true;

    this.updateContent(editor, this.props.value, this.props.subeditor);
    // this.setState({ editor: editor });
    this.setState({ editor: editor }, () => {
      if (this.props.reviewOnly) return;

      this._onReplaceWidgetState = (e) => {
        const { oldUid, nextState } = e.detail || {};
        if (!oldUid || !nextState) return;
        if (!this.state?.editor) return;

        const ed = this.state.editor;
        const root = ed?.$el?.get?.(0) || ed?.el;
        if (!root) return;

        this.setState(
          (prev) => {
            const nextEditors = { ...(prev.editors || {}) };
            delete nextEditors[oldUid];
            return { editors: nextEditors };
          },
          () => {
            const ph = root.querySelector(
              `.widget-placeholder#${CSS.escape(oldUid)}`
            );
            if (!ph) return;

            try {
              delete ph.state;
            } catch (_) {
              ph.state = undefined;
            }

            ph.id = nextState.uid;
            ph.innerHTML = `<div data-open="SOW" data-state="${encode(
              JSON.stringify(nextState)
            )}"><div data-closed="EOW"></div></div>`;

            ed.undo?.saveStep?.();

            ed.events?.trigger?.('html.set');

            setTimeout(() => {
              orderWidgets();
              ed.undo?.reset?.();
            }, 0);
          }
        );
      };

      window.addEventListener(
        'froala:replace-widget-state',
        this._onReplaceWidgetState
      );

      this._onUngroupWidget = (e) => {
        const { oldUid, nextWidgets } = e.detail || {};
        if (!oldUid || !Array.isArray(nextWidgets) || !nextWidgets.length)
          return;
        if (!this.state?.editor) return;

        const ed = this.state.editor;
        const root = ed?.$el?.get?.(0) || ed?.el;
        if (!root) return;

        this.setState(
          (prev) => {
            const nextEditors = { ...(prev.editors || {}) };
            delete nextEditors[oldUid];
            return { editors: nextEditors };
          },
          () => {
            const ph = root.querySelector(
              `.widget-placeholder#${CSS.escape(oldUid)}`
            );
            if (!ph) return;

            const html = nextWidgets
              .map((st) => {
                const stateStr = encode(JSON.stringify(st));
                return (
                  `<p><br></p>` +
                  `<div style="position:relative" contenteditable=false class="widget-placeholder fr-deletable" id="${st.uid}">` +
                  `<div data-open="SOW" data-state="${stateStr}"><div data-closed="EOW"></div></div>` +
                  `</div>` +
                  `<p><br></p>`
                );
              })
              .join('');

            ph.insertAdjacentHTML('beforebegin', html);
            ph.remove();

            ed.undo?.saveStep?.();
            ed.events?.trigger?.('html.set');

            setTimeout(() => {
              orderWidgets();
              ed.undo?.reset?.();
            }, 0);
          }
        );
      };

      this._onMoveWidgetToGroup = async (e) => {
        const { widgetUid, groupUid } = e.detail || {};
        if (!widgetUid || !groupUid) return;
        if (!this.state?.editor) return;

        const ed = this.state.editor;
        const root = ed?.$el?.get?.(0) || ed?.el;
        if (!root) return;

        const readStateFromDom = (uid) => {
          const ph = root.querySelector(
            `.widget-placeholder#${CSS.escape(uid)}`
          );
          if (!ph) return null;

          const node = ph.querySelector('div[data-state],span[data-state]');
          if (!node) return null;

          const stateStr = node.getAttribute('data-state');
          if (!stateStr) return null;

          try {
            return JSON.parse(stateStr);
          } catch (_) {
            try {
              return JSON.parse(decode(stateStr));
            } catch (_e) {
              return null;
            }
          }
        };

        const wEditor = this.state.editors?.[widgetUid];
        const widgetState = wEditor?.stateFn
          ? await wEditor.stateFn()
          : readStateFromDom(widgetUid);
        if (!widgetState) return;

        const gEditor = this.state.editors?.[groupUid];
        const groupState = gEditor?.stateFn
          ? await gEditor.stateFn()
          : readStateFromDom(groupUid);
        if (!groupState || !groupState.isGroup) return;

        const nextGroupState = deep_copy(groupState);
        nextGroupState.items = Array.isArray(nextGroupState.items)
          ? nextGroupState.items
          : [];

        if (!nextGroupState.items.some((it) => it?.uid === widgetState.uid)) {
          nextGroupState.items.push({
            ...deep_copy(widgetState),
            type: widgetState?.type,
          });
        }

        nextGroupState.showCount = Math.max(
          1,
          Math.min(nextGroupState.showCount || 1, nextGroupState.items.length)
        );

        this.setState(
          (prev) => {
            const nextEditors = { ...(prev.editors || {}) };
            delete nextEditors[widgetUid];
            delete nextEditors[groupUid];
            return { editors: nextEditors };
          },
          () => {
            // Remove standalone widget placeholder
            const wPh = root.querySelector(
              `.widget-placeholder#${CSS.escape(widgetUid)}`
            );
            wPh?.remove();

            // Update group placeholder DOM state
            const gPh = root.querySelector(
              `.widget-placeholder#${CSS.escape(groupUid)}`
            );
            if (!gPh) return;

            try {
              delete gPh.state;
            } catch (_) {
              gPh.state = undefined;
            }

            gPh.innerHTML = `<div data-open="SOW" data-state="${encode(
              JSON.stringify(nextGroupState)
            )}"><div data-closed="EOW"></div></div>`;

            ed.undo?.saveStep?.();
            ed.events?.trigger?.('html.set');

            setTimeout(() => {
              orderWidgets();
              ed.undo?.reset?.();
            }, 0);
          }
        );
      };

      window.addEventListener('froala:ungroup-widget', this._onUngroupWidget);
      window.addEventListener(
        'froala:move-widget-to-group',
        this._onMoveWidgetToGroup
      );
    });
  }

  findWidgets = async () => {
    if (!this.state?.editor) return;

    const root = this.state.editor.el;
    const placeholders = root.querySelectorAll('.widget-placeholder');

    // 1) зібрати актуальні стани з DOM
    const domStatesByUid = {};
    placeholders.forEach((ph) => {
      const node = ph.querySelector('div[data-state],span[data-state]');
      if (!node) return;

      const stateStr = node.getAttribute('data-state');
      if (!stateStr) return;

      let st;
      try {
        st = JSON.parse(stateStr); // legacy
      } catch (e) {
        try {
          st = JSON.parse(decode(stateStr)); // base64
        } catch (_) {
          return;
        }
      }
      if (!st?.uid) return;

      domStatesByUid[st.uid] = { state: st, placeholder: ph };
      // кеш як у тебе було (але не обов'язково)
      ph.state = st;
    });

    // 2) merge у React state: не чіпаємо існуючі портали, додаємо лише нові/змінені
    this.setState((prev) => {
      const prevEditors = prev.editors || {};
      const nextEditors = { ...prevEditors };

      // remove editors, яких більше нема в DOM
      Object.keys(nextEditors).forEach((uid) => {
        if (!domStatesByUid[uid]) {
          delete nextEditors[uid];
        }
      });

      // add editors, яких ще нема в state
      Object.entries(domStatesByUid).forEach(
        ([uid, { state, placeholder }]) => {
          if (!nextEditors[uid]) {
            nextEditors[uid] = renderEditor(
              state,
              placeholder,
              this.state.editor.undo,
              this.lessonId
            );
          }
        }
      );

      return { contentReady: true, editors: nextEditors };
    });
  };

  renderWidget(state, target, openEditor = false, i = 0) {
    //renderWidget.call(this, state, target, openEditor, i);
  }

  insertWidget = (_context, initialState, _cb) => {
    let uid = newUid(20);
    let _this = this;
    uid = this.props.parentuid ? this.props.parentuid + '_' + uid : uid;

    let newState = deep_copy(initialState);
    newState.uid = uid;

    this.state.editor.html.insert(
      '<p><br></p><div style="position:relative" contenteditable=false class="widget-placeholder fr-deletable" id="' +
        uid +
        '"></div><p><br></p>',
      false
    );

    this.setState(
      {
        editors: update(this.state.editors, {
          [uid]: {
            $set: renderEditor(
              newState,
              findElementInEditor(this.state.editor, uid),
              this.state.editor.undo,
              _this.lessonId
            ),
          },
        }),
      },
      () => {
        _this.props.onModified();
      }
    );

    //renderWidget(newState, document.getElementById(newState.uid), true);
    //this.onUpdate();
  };

  insertChatGpt = () => {
    let uid = newUid(20);
    uid = this.props.parentuid ? this.props.parentuid + '_' + uid : uid;

    let newState = {};
    newState.uid = uid;

    this.state.editor.html.insert(
      '<p><br></p><div style="position: relative" class="chat-gpt-placeholder fr-deletable" id="' +
        uid +
        '"></div><p><br></p>',
      false
    );

    this.setState({
      chatGpt: update(this.state.chatGpt, {
        [uid]: {
          $set: renderChatGpt(
            newState,
            findElementInEditor(this.state.editor, uid)
          ),
        },
      }),
    });

    this.state.editor.undo.reset();
  };

  insertInlineWidget = (_context, initialState, _cb) => {
    let uid = newUid(20);
    let _this = this;
    uid = this.props.parentuid ? this.props.parentuid + '_' + uid : uid;

    let newState = deep_copy(initialState);
    newState.uid = uid;

    this.state.editor.html.insert(
      '<span style="position:relative" contenteditable=false class="inline-block widget-placeholder fr-deletable" id="' +
        uid +
        '"></span>',
      false
    );

    this.setState(
      {
        editors: update(this.state.editors, {
          [uid]: {
            $set: renderEditor(
              newState,
              findElementInEditor(this.state.editor, uid),
              this.state.editor.undo,
              _this.lessonId
            ),
          },
        }),
      },
      () => {
        _this.props.onModified();
      }
    );
  };

  insertPhet = (el) => {
    let uid = newUid(20);
    uid = this.props.parentuid ? this.props.parentuid + '_' + uid : uid;

    this.state.editor.html.insert(
      '<p><br></p><div style="position: relative" class="chat-gpt-placeholder fr-deletable" id="' +
        uid +
        '">' +
        el +
        '</div><p><br></p>',
      false
    );
  };

  moreExec(cmd) {
    var $btn = this.$tb.find('[data-cmd='.concat(cmd, ']'));
    this.toggleMoreButton($btn);
    this.toolbar.setMoreToolbarsHeight();
  }

  toggleMoreButton($btn) {
    var $buttonGroup = this.$tb.find(
      '.fr-more-toolbar[data-name="'.concat($btn.attr('data-group-name'), '"]')
    );
    this.$tb.find('.fr-open').not($btn).removeClass('fr-open');
    $btn.toggleClass('fr-open');

    this.$tb.find('.fr-more-toolbar').removeClass('fr-overflow-visible');

    if (this.$tb.find('.fr-expanded').not($buttonGroup).length) {
      this.$tb.find('.fr-expanded').toggleClass('fr-expanded');

      $buttonGroup.toggleClass('fr-expanded');
    } else {
      $buttonGroup.toggleClass('fr-expanded');
      this.$box.toggleClass('fr-toolbar-open');
      this.$tb.toggleClass('fr-toolbar-open');
    }
  }

  insertEmbedWidget = (_context, initialState, _cb) => {
    let editor = this.state.editor;
    let uid = newUid(20);
    uid = this.props.parentuid ? this.props.parentuid + '_' + uid : uid;
    //editor.html.insert('<span contenteditable="false" draggable="true" class="fr-video fr-deletable fr-draggable widget-placeholder" id="' + uid + '" style="display: block; clear: both; text-align: center;">HELLO WORLD</span>')
    editor.html.insert(
      '<span contenteditable="false" draggable="true" class="fr-video fr-deletable fr-draggable" id="' +
        uid +
        '" style="display: block; clear: both; text-align: center;"></span>'
    );

    editor.events.focus();
    editor.selection.setAtEnd(editor.$el.get(0));
    //editor.selection.restore();
    let newState = deep_copy(initialState);
    newState.uid = uid;
    setTimeout(
      () =>
        this.renderWidget(
          newState,
          document.getElementById(newState.uid),
          true
        ),
      0
    );
    this.onUpdate();
  };

  onUpdate = async (...params) => {
    //return onUpdate.call(this, ...params);
  };

  sync = async (...params) => {
    return sync.call(this, ...params);
  };

  updateContent = (editor, value, subeditor) => {
    const _this = this;
    const lessonId = _this.lessonId;
    if (!this.value_initialized && value !== undefined && this.myRef.current) {
      setTimeout(() => {
        if (editor) {
          this.value_initialized = true;
          const normalizedValue = normalizeVideoHtml(value);
          window.$(editor.el).html(normalizedValue);
          window.$('.remove_me_gEE8QIPZ6LEay22SdSCU').remove();

          var editor_node = editor.el;
          normalizeVideoIframes(editor_node);
          const imgElements = editor_node.querySelectorAll('img');
          const videoElements = editor_node.querySelectorAll('video');
          if (
            !editor_node.innerText.trim() &&
            !imgElements?.length > 0 &&
            !videoElements?.length > 0
          ) {
            editor.placeholder.show();
          } else {
            editor.placeholder.hide();
          }
          if (subeditor) {
            editor.el.parentNode.contentEditable = 'true';
            var node = editor.el;
            if (
              !node.innerHTML.trim() ||
              (node.childNodes.length === 1 &&
                node.firstChild.nodeName === 'P' &&
                !node.firstChild.getAttribute('contenteditable'))
            ) {
              const pElement = document.createElement('p');
              pElement.setAttribute('contenteditable', 'true');
              node.appendChild(pElement);
            } else {
              const imgElements = node.querySelectorAll('img');
              const pElements = node.querySelectorAll('p');

              pElements.forEach((text) => {
                const isTextAlreadyWrapped =
                  text.getAttribute('contenteditable') === 'true';

                if (isTextAlreadyWrapped) {
                  text.removeAttribute('contenteditable');
                }
              });

              imgElements.forEach((img) => {
                const isAlreadyWrapped =
                  img.parentElement.tagName === 'SPAN' &&
                  img.parentElement.getAttribute('contenteditable') === 'true';

                if (!isAlreadyWrapped) {
                  const span = document.createElement('span');
                  span.classList.add('your-custom-class');
                  span.setAttribute('contenteditable', 'true');
                  img.parentNode.replaceChild(span, img);
                  span.appendChild(img);
                }
              });
            }
          }

          const newWidgets = Object.values(
            this.props.reviewOnly
              ? renderWidgets(
                  editor.el,
                  {},
                  {
                    correctionType: 'automatic',
                    courseStatus: 'disabled',
                    isCorrected: false,
                    showAnswers: true,
                  },
                  this.props.authUser?.id
                )
              : renderEditors.call(null, editor.el, editor.undo, lessonId)
          ).filter((x) => x);

          this.setState(
            {
              contentReady: true,
              editors: newWidgets.reduce((acc, el) => {
                acc[el.state.uid] = el;
                return acc;
              }, {}),
            },
            () => {
              if (this.props.reviewOnly) {
                editor.edit?.off?.();
              }
              if (this.props.onLoaded) this.props.onLoaded();
            }
          );
        }
        if (this.props.modus == 'lessonEdit' && !this.props.reviewOnly) {
          editor.events.focus();
        }
      }, 0);
    }
    if (!this.value_initialized && value === undefined && this.myRef.current) {
      setTimeout(() => {
        if (editor) {
          if (subeditor) {
            editor.el.parentNode.contentEditable = 'true';
            var node = editor.el;
            if (
              !node.innerHTML.trim() ||
              (node.childNodes.length === 1 &&
                node.firstChild.nodeName === 'P' &&
                !node.firstChild.getAttribute('contenteditable'))
            ) {
              const pElement = document.createElement('p');
              pElement.setAttribute('contenteditable', 'true');
              node.appendChild(pElement);
            }
          }
        }
      }, 0);
    }
    window.$(this.myRef.current).find('.fancybox > img').unwrap();
  };

  componentDidUpdate = (prevProps, prevState) => {
    if (prevState.editors !== this.state.editors) {
      const safeEditors = Object.fromEntries(
        Object.entries(this.state.editors || {}).filter(([, v]) => Boolean(v))
      );
      this.props.onEditors && this.props.onEditors(safeEditors);
    }

    if (prevProps.courseLang !== this.props.courseLang) {
      const editor = this.editorRef.current;
      if (editor) {
        editor.opts.courseLang = this.props.courseLang || 'en';
      }
    }
  };

  shouldComponentUpdate = (nextProps, nextState) => {
    return true;
    /*
    if (nextProps.value !== this.props.value) return true;
    if (nextState.showWebcamPicture != this.state.showWebcamPicture) return true;
    if (nextState.showWebcamVideo != this.state.showWebcamVideo) return true;
    return false;
    */
  };

  render() {
    let {
      mode,
      hideToolbar,
      value,
      toolbarSticky,
      t,
      tReady,
      i18n,
      dispatch,
      onSave,
      saveWidget,
      widgetsRendered,
      setsyncfunction,
      setKofferLes,
      isSubmitted,
      setRef,
      blockSaveOnUnmount,
      onModified,
      onBlur,
      onEditors,
      onLoaded,
      lessonId,
      reviewOnly,
      classTextarea,
      contentLinkTargets,
      ...other
    } = this.props;

    return (
      <Fragment>
        {this.state.contentReady && this.state.renderWidgets}
        {Object.values(this.state.editors)
          .filter(Boolean)
          .map((x) => x?.portal)}
        {Object.values(this.state.views)}
        {Object.values(this.state.chatGpt)
          .filter(Boolean)
          .map((x) => x?.portal)}
        {this.state.showWebcamPicture && (
          <CameraModal
            setIsOpenPhotoModal={(isOpen) =>
              this.setState({ showWebcamPicture: isOpen })
            }
            isOpenPhotoModal={this.state.showWebcamPicture}
            setUploadFile={(resultFn) => {
              let [blob] = resultFn([]);
              this.state.editor.image.upload([blob]);
              this.setState({ showWebcamPicture: false });
            }}
          ></CameraModal>
        )}
        {this.state.showPictureLibrary && (
          <LibraryModal
            setIsOpenLibraryModal={(isOpen) =>
              this.setState({ showPictureLibrary: isOpen })
            }
            isOpenLibraryModal={this.state.showPictureLibrary}
            lessonPage={true}
            setUploadFile={(resultFn) => {
              let [blob] = resultFn([]);
              this.state.editor.image.upload([blob]);
              this.setState({ showWebcamPicture: false });
            }}
            onSelectImage={(imageUrl) => {
              const editor = this.state.editor;
              if (!editor || !imageUrl) return;

              editor.events.focus();
              editor.selection.restore?.();
              editor.image.insert(imageUrl, true, null, null, null);
              editor.undo.saveStep();
              editor.events.trigger('contentChanged');
              this.setState({ showPictureLibrary: false });
            }}
          />
        )}
        {this.state.showWebcamVideo && (
          <VideoCameraModal
            setIsOpenVideoModal={(isOpen) => {
              this.setState({ showWebcamVideo: isOpen });
            }}
            isOpenVideoModal={this.state.showWebcamVideo}
            setUploadFile={(resultFn) => {
              let result = resultFn([]);
              let file = new File(
                result.map((x) => x.blob),
                `camera.${result[0].mimeType}`,
                {
                  type: 'video/webm;codecs=vp9',
                }
              );
              this.state.editor.video.upload([file]);
              this.setState({ showWebcamVideo: false });
            }}
          ></VideoCameraModal>
        )}

        {this.state.showDrawIoModal && (
          <DrawIoImportModal
            onClose={() => this.setState({ showDrawIoModal: null })}
            onInsert={(file) => {
              uploadFile(`/public/${newUid(20)}`, file).then((url) => {
                const encoded = encodeURIComponent(url);
                const fileName = file.name;
                this.state.editor.html.insert(
                  `<a href="https://app.diagrams.net/#U${encoded}" target="_blank" rel="noopener noreferrer">${fileName}</a>`,
                  false
                );
                this.setState({ showDrawIoModal: null });
              });
            }}
          ></DrawIoImportModal>
        )}

        {this.state.showContentLinkModal && (
          <ContentLinkModal
            targets={this.props.contentLinkTargets}
            initialText={this.state.showContentLinkModal.text}
            onClose={() => this.setState({ showContentLinkModal: null })}
            onInsert={(html) => {
              const editor = this.state.editor;
              editor.selection.restore();
              editor.html.insert(html, false);
              editor.undo.saveStep();
              editor.events.trigger('contentChanged');
              this.setState({ showContentLinkModal: null });
            }}
          />
        )}

        {this.state.showImageLibraryForSelectedImage && (
          <AddImageToLibrary
            imageUrl={this.state.selectedImageUrl}
            isOpenAddLibraryModal={this.state.showImageLibraryForSelectedImage}
            setIsOpenAddLibraryModal={(isOpen) =>
              this.setState({
                showImageLibraryForSelectedImage: isOpen,
              })
            }
          />
        )}

        <div
          id={this.state.uid}
          className={`${
            this.props.modus === 'lessonEdit' && 'fr-editor-block'
          } relative z-0 rounded-lg border border-grey-02 ${
            this.props.className || ''
          }`}
        >
          <textarea
            className={classTextarea}
            {...other}
            ref={this.myRef}
          ></textarea>
        </div>
      </Fragment>
    );
  }
}

function FroalaTextareaWithCourseLang(props) {
  const { courseLang } = useContext(CourseLanguageContext);
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;

  return (
    <FroalaTextarea {...props} courseLang={courseLang} authUser={authUser} />
  );
}

const connectFroalaTextarea = withTranslation()(FroalaTextareaWithCourseLang);
export default connectFroalaTextarea;
